import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

// Per-query/mutation metadata (STD-015). `errorMessage` is the i18n fallback the
// global error toast falls back to when the error carries no usable message;
// `skipGlobalErrorToast` opts a query/mutation out of the global toast (e.g. when
// a component handles the error inline itself).
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { errorMessage?: string; skipGlobalErrorToast?: boolean };
    mutationMeta: { errorMessage?: string; skipGlobalErrorToast?: boolean };
  }
}

// One place turns server errors into toasts so components don't each wire up an
// error->toast effect (STD-015). Components still render inline error UI from
// `query.isError` / `mutation.isError` where they want persistent context.
const toastError = (error: unknown, fallback?: string) => {
  toast.error(getErrorMessage(error, fallback), { duration: 5000 });
};

// Shared TanStack Query client (STD-015). Conservative defaults: data is fresh
// for 60s (cuts the redundant-refetch-on-tab-flip problem) and we don't refetch
// on window focus, which would surprise users on a dense dashboard. Individual
// queries override these per-domain when they need to.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.skipGlobalErrorToast) return;
      toastError(error, query.meta?.errorMessage);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.skipGlobalErrorToast) return;
      toastError(error, mutation.meta?.errorMessage);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
